/**
 * SLA Timer Types (PROJ-007)
 * TypeScript definitions for SLA timer management
 */

/**
 * SLA Stage enum - matches backend SLA_STAGES
 */
export type SlaStage = 'response' | 'investigation' | 'resolution';

/**
 * SLA Stage Labels for UI display
 */
export const SLA_STAGE_LABELS: Record<SlaStage, string> = {
  response: 'Respuesta Inicial',
  investigation: 'Investigación Completa',
  resolution: 'Resolución Final'
};

/**
 * SLA Stage Descriptions
 */
export const SLA_STAGE_DESCRIPTIONS: Record<SlaStage, string> = {
  response: 'Tiempo para asignar o reconocer el incidente',
  investigation: 'Tiempo para completar la investigación',
  resolution: 'Tiempo para resolver completamente el incidente'
};

/**
 * SLA Timer
 * Represents a single SLA timer for an incident stage
 */
export interface SlaTimer {
  id: string;
  incidentId: string;
  stage: SlaStage;
  targetAt: string; // ISO timestamp
  breached: boolean;
  breachedAt: string | null; // ISO timestamp
  remainingHours: number;
  isWarning: boolean; // true if less than 2 hours remaining
  isExpired: boolean; // true if remaining time is 0
}

/**
 * SLA Statistics for an incident
 */
export interface SlaStats {
  total: number;
  breached: number;
  onTrack: number;
  byStage: Record<SlaStage, {
    breached: boolean;
    targetAt: string;
    breachedAt?: string;
  }>;
}

/**
 * SLA Warning
 * Timer in warning state (near expiration) with incident context
 */
export interface SlaWarning {
  id: string;
  incidentId: string;
  stage: SlaStage;
  targetAt: string;
  breached: boolean;
  breachedAt: string | null;
  remainingHours: number;
  isWarning: boolean;
  isExpired: boolean;
  incident: {
    id: string;
    externalRef: string | null;
    summary: string | null;
    severity: string;
    state: string;
  };
}

/**
 * SLA Rules Configuration (for display/reference)
 */
export interface SlaRulesConfig {
  response: Record<string, number>; // hours by severity
  investigation: Record<string, number>;
  resolution: Record<string, number>;
}

/**
 * SLA Rules - matches backend configuration
 */
export const SLA_RULES: SlaRulesConfig = {
  response: {
    critical: 1,
    high: 4,
    medium: 8,
    low: 24
  },
  investigation: {
    critical: 24,
    high: 48,
    medium: 120,
    low: 240
  },
  resolution: {
    critical: 72,
    high: 168,
    medium: 336,
    low: 720
  }
};

/**
 * Get SLA status badge variant based on timer state
 */
export function getSlaStatusVariant(timer: SlaTimer): 'default' | 'secondary' | 'warning' | 'destructive' {
  if (timer.breached) return 'destructive';
  if (timer.isWarning) return 'warning';
  if (timer.isExpired) return 'destructive';
  return 'default';
}

/**
 * Get SLA status color for UI indicators
 */
export function getSlaStatusColor(timer: SlaTimer): string {
  if (timer.breached) return 'text-red-500';
  if (timer.isWarning) return 'text-yellow-500';
  if (timer.isExpired) return 'text-red-600';
  return 'text-green-500';
}

/**
 * Format remaining time for display
 */
export function formatRemainingTime(hours: number): string {
  if (hours < 0) return 'Vencido';
  if (hours < 1) {
    const minutes = Math.floor(hours * 60);
    return `${minutes}m`;
  }
  if (hours < 24) {
    return `${Math.floor(hours)}h`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = Math.floor(hours % 24);
  return `${days}d ${remainingHours}h`;
}

/**
 * Get SLA target hours for a given severity and stage
 */
export function getSlaTargetHours(severity: string, stage: SlaStage): number {
  return SLA_RULES[stage][severity.toLowerCase()] || 0;
}
